// umlaut-check: skip — technische Bezeichner (Pruefung, pruefung.*) bleiben ASCII
import React, {useState} from "react";
import {Button, Card, Modal, OverlayTrigger, Spinner, Table, Tooltip} from "react-bootstrap";
import {Link, router} from "@inertiajs/react";
import axios from "axios";
import Icon from "@mdi/react";
import {mdiCheck, mdiCogOutline, mdiEmailOutline, mdiOpenInNew, mdiRefresh, mdiEyeOutline, mdiCloseCircleOutline} from "@mdi/js";
import HelpTooltip from "../../Components/HelpTooltip.jsx";
import SelectList from "../../Components/SelectList.jsx";
import {TOOLTIP_DELAY, fixedPopper} from "../../Components/tooltipConfig.js";

/**
 * Dashboard „Meine offenen Vorgänge“ (Plan #82, Stufe 1). Zwei Bausteine:
 * offene Aufträge ohne Bearbeitung seit der Frist und Rechnungen ohne
 * offenen Posten. Antworten laufen im Hintergrund (kein Neuladen der Seite);
 * schlägt das Speichern fehl, bleibt die Zeile mit Fehlermeldung stehen.
 * Leere Bausteine verschwinden vollständig.
 */

const AUFTRAG_STATUS = {
    ORDER_ENTRY_IN_PROGRESS: "In Bearbeitung",
    ORDER_CONFIRMATION_PRINTED: "Bestätigung gedruckt",
};
const RECHNUNG_STATUS = {
    NEW: "Entwurf",
    ENTRY_COMPLETED: "Erfassung abgeschlossen",
    DOCUMENT_CREATED: "Beleg erzeugt",
};
const RECHNUNGSART = {
    STANDARD_INVOICE: "Rechnung",
    CREDIT_NOTE: "Gutschrift",
    ADVANCE_PAYMENT_INVOICE: "Anzahlungsrechnung",
    PREPAYMENT_INVOICE: "Vorkasserechnung",
    FINAL_INVOICE: "Schlussrechnung",
};
const GRUPPE = {
    container: "Container/Palette",
    endkunde: "Endkunde",
    handel: "Handel, Key-Account, Industrie",
    zoll: "Zoll-Proforma",
};

const datum = (iso) => iso ? new Date(iso.length === 10 ? iso + "T12:00:00" : iso).toLocaleDateString("de-DE", {timeZone: "Europe/Berlin", day: "2-digit", month: "2-digit", year: "numeric"}) : "–";
const zeitpunkt = (iso) => iso ? new Date(iso).toLocaleString("de-DE", {timeZone: "Europe/Berlin", weekday: "short", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"}) + " Uhr" : "noch nie";
const betrag = (wert, waehrung) => wert === null || wert === undefined ? "–" : Number(wert).toLocaleString("de-DE", {style: "currency", currency: waehrung || "EUR"});

function Tipp({text, children, placement = "top"}) {
    return (
        <OverlayTrigger placement={placement} delay={TOOLTIP_DELAY} popperConfig={fixedPopper()} overlay={<Tooltip>{text}</Tooltip>}>
            {children}
        </OverlayTrigger>
    );
}

function Kopf({titel, hilfe}) {
    return (
        <th scope="col">
            <span className="pv-kopf">{titel}<HelpTooltip placement="top" size={0.7}>{hilfe}</HelpTooltip></span>
        </th>
    );
}

function Aktionen({fall, art, onAntwort, weclappUrl}) {
    const jaText = art === "auftrag" ? "Ja, berechtigt offen" : "Ja, bewusst noch ohne Posten";
    const jaTipp = art === "auftrag"
        ? "Der Auftrag soll offen bleiben. Er verschwindet für die eingestellte Ruhezeit und kommt danach wieder, falls er dann noch offen ist."
        : "Die Rechnung soll noch ohne offenen Posten bleiben. Sie verschwindet für die eingestellte Ruhezeit und kommt danach wieder, falls dann noch kein Posten erzeugt ist.";
    const neinTipp = art === "auftrag"
        ? "Öffnet den Auftrag in Weclapp in einem neuen Tab, damit du ihn dort schließt. Hier bleibt er mit dem Vermerk „in Weclapp noch zu erledigen“ stehen, bis Weclapp ihn als geschlossen meldet."
        : "Öffnet die Rechnung in Weclapp in einem neuen Tab, damit du dort den offenen Posten erzeugst. Hier bleibt sie mit Vermerk stehen, bis Weclapp den Posten meldet.";

    return (
        <div className="pv-aktionen">
            <Tipp text={jaTipp}>
                <Button size="sm" variant="outline-success" disabled={fall._laeuft} onClick={() => onAntwort(fall, "ja")}>
                    <Icon path={mdiCheck} size={0.65} className="me-1"/>{jaText}
                </Button>
            </Tipp>
            <Tipp text={neinTipp}>
                <Button size="sm" variant="outline-secondary" disabled={fall._laeuft} onClick={() => {
                    // Synchron im Klick öffnen, sonst blockt der Browser das neue Fenster.
                    window.open(weclappUrl, "_blank", "noopener");
                    onAntwort(fall, "nein");
                }}>
                    Nein<Icon path={mdiOpenInNew} size={0.6} className="ms-1"/>
                </Button>
            </Tipp>
            {fall._laeuft && <Spinner animation="border" size="sm" className="ms-1" role="status"/>}
        </div>
    );
}

function Zeilenvermerke({fall}) {
    return (
        <>
            {fall.zustand === "nein" && (
                <Tipp text="Du hast „Nein“ gesagt. Der Vorgang bleibt hier sichtbar und zählt weiter, bis er in Weclapp erledigt ist.">
                    <span className="pv-chip pv-chip--offen" tabIndex={0}>in Weclapp noch zu erledigen</span>
                </Tipp>
            )}
            {fall.ohne_zuordnung && (
                <Tipp text="Der in Weclapp eingetragenen Person ist kein Konto im Ordermanagement zugeordnet (Feld „Weclapp-ID“ am Benutzer). Nur du siehst diesen Vorgang; er zählt für keine Wochenmail.">
                    <span className="pv-chip pv-chip--warn" tabIndex={0}>ohne zuständiges Konto</span>
                </Tipp>
            )}
            {fall._fehler && (
                <div className="pv-fehler" role="alert">
                    <Icon path={mdiCloseCircleOutline} size={0.6} className="me-1"/>{fall._fehler}
                </div>
            )}
        </>
    );
}

function Ampel({fall}) {
    const text = fall.ampel === "rot"
        ? `Rot: seit ${fall.tage_ohne_bearbeitung} Tagen ohne Bearbeitung – die Rot-Frist der Gruppe „${GRUPPE[fall.gruppe]}“ ist überschritten.`
        : `Gelb: seit ${fall.tage_ohne_bearbeitung} Tagen ohne Bearbeitung – die Gelb-Frist der Gruppe „${GRUPPE[fall.gruppe]}“ ist überschritten.`;
    return (
        <Tipp text={text}>
            <span className={`pv-ampel pv-ampel--${fall.ampel}`} tabIndex={0} aria-label={text}/>
        </Tipp>
    );
}

/**
 * Vorführansicht: zeigt die echte Wochenmail wie einen Screenshot. Der iframe
 * trennt die Mail-Stile von der Seite; Links öffnen in einem neuen Tab. Die
 * Höhe folgt der Mail, damit nur der Dialog scrollt. Es wird nichts verschickt.
 */
function WochenmailBeispiel({offen, onSchliessen}) {
    const [zustand, setZustand] = useState({art: "laedt"});
    const [hoehe, setHoehe] = useState(640);

    const laden = async () => {
        setZustand({art: "laedt"});
        try {
            const {data} = await axios.get("/dashboard/vorgaenge/wochenmail-beispiel");
            setZustand(data.html ? {art: "fertig", ...data} : {art: "leer"});
        } catch (e) {
            setZustand({art: "fehler"});
        }
    };

    const srcDoc = zustand.html
        ? (/<head[^>]*>/i.test(zustand.html)
            ? zustand.html.replace(/<head[^>]*>/i, (kopf) => `${kopf}<base target="_blank">`)
            : `<base target="_blank">${zustand.html}`)
        : undefined;

    const hoeheMessen = (e) => {
        const dokument = e.currentTarget.contentDocument;
        if (dokument?.documentElement) {
            setHoehe(dokument.documentElement.scrollHeight);
        }
    };

    return (
        <Modal show={offen} onShow={laden} onHide={onSchliessen} size="lg" centered scrollable aria-labelledby="pv-mail-titel" dialogClassName="pv-mail-dialog">
            <Modal.Header closeButton closeLabel="Schließen">
                <Modal.Title id="pv-mail-titel" as="h2" className="pv-mail-titel">Beispiel der Wochenmail</Modal.Title>
            </Modal.Header>
            <Modal.Body className="pv-mail-flaeche">
                {zustand.art === "laedt" && (
                    <div className="pv-mail-hinweis" role="status"><Spinner animation="border" size="sm" className="me-2"/>Die Mail wird aufgebaut …</div>
                )}
                {zustand.art === "leer" && (
                    <div className="pv-mail-hinweis" role="status">Gerade ist nichts offen, deshalb gäbe es diese Woche keine Mail.</div>
                )}
                {zustand.art === "fehler" && (
                    <div className="pv-mail-hinweis" role="alert">
                        <span className="pv-fehler"><Icon path={mdiCloseCircleOutline} size={0.6} className="me-1"/>Die Mail konnte nicht geladen werden.</span>
                        <Button size="sm" variant="outline-secondary" className="ms-3" onClick={laden}>Noch einmal versuchen</Button>
                    </div>
                )}
                {zustand.art === "fertig" && (
                    <div className="pv-mail-blatt">
                        <dl className="pv-mail-kopf">
                            <dt>Betreff</dt><dd>{zustand.betreff}</dd>
                            <dt>Von</dt><dd>{zustand.absender}</dd>
                        </dl>
                        <iframe
                            title="Beispiel der Wochenmail"
                            className="pv-mail-rahmen"
                            sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                            srcDoc={srcDoc}
                            style={{height: hoehe}}
                            onLoad={hoeheMessen}
                        />
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onSchliessen}>Schließen</Button>
            </Modal.Footer>
        </Modal>
    );
}

export default function MeineVorgaenge({
    auftraege: startAuftraege,
    rechnungen: startRechnungen,
    letzterAbgleich,
    weclappBaseUrl,
    vorfuehrung,
    begruessung,
    istSuperadmin,
    ansichtAls,
    personen,
    ruhezeitTage,
    nurSuperadmin,
}) {
    const [auftraege, setAuftraege] = useState(startAuftraege);
    const [rechnungen, setRechnungen] = useState(startRechnungen);
    const [meldung, setMeldung] = useState(null);
    const [abgleichLaeuft, setAbgleichLaeuft] = useState(false);
    const [mailOffen, setMailOffen] = useState(false);

    const basis = (weclappBaseUrl || "https://molotow.weclapp.com/").replace(/\/?$/, "/");
    const weclappUrl = (fall) => fall.art === "auftrag"
        ? `${basis}app/sales-order/${fall.weclapp_id}`
        : `${basis}app/sales-invoice/${fall.weclapp_id}`;

    const aendern = (art, id, aenderung) => {
        const setzen = art === "auftrag" ? setAuftraege : setRechnungen;
        setzen((liste) => aenderung === null
            ? liste.filter((f) => f.id !== id)
            : liste.map((f) => f.id === id ? {...f, ...aenderung} : f));
    };

    // Meldungstexte für „Ja“ und „Nein“ (Auftrag #1588): sagen, was jetzt
    // passiert, nicht nur, was gespeichert wurde.
    const meldungFuer = (fall, antwort) => {
        if (antwort === "ja") {
            return fall.art === "auftrag"
                ? `${fall.nummer} bleibt offen. Für diesen Auftrag kommt ${ruhezeitTage} Tage lang keine Erinnerung.`
                : `${fall.nummer} bleibt vorerst ohne offenen Posten. Für diese Rechnung kommt ${ruhezeitTage} Tage lang keine Erinnerung.`;
        }
        return fall.art === "auftrag"
            ? `Bitte schließe ${fall.nummer} in Weclapp ab. Bis dahin bleibt er auf deiner Liste.`
            : `Bitte erzeuge in Weclapp den offenen Posten für ${fall.nummer}. Bis dahin bleibt sie auf deiner Liste.`;
    };

    const onAntwort = async (fall, antwort) => {
        aendern(fall.art, fall.id, {_laeuft: true, _fehler: null});
        try {
            const pfad = vorfuehrung ? "vorfuehrung" : "antwort";
            const {data} = await axios.post(`/dashboard/vorgaenge/${fall.id}/${pfad}`, {antwort});
            // In der Vorführansicht verschwindet die Zeile auch bei „Nein“,
            // damit der Ablauf im Meeting eindeutig ist; sonst bleibt sie mit
            // Vermerk stehen, bis Weclapp den Vorgang schließt.
            if (antwort === "ja" || vorfuehrung) {
                aendern(fall.art, fall.id, null);
            } else {
                aendern(fall.art, fall.id, {_laeuft: false, zustand: "nein"});
            }
            const text = meldungFuer(fall, antwort);
            setMeldung(data.demo && !vorfuehrung ? `Demomodus, nichts gespeichert – ${text}` : text);
            if (!data.demo) {
                router.reload({only: ["menuEntries"]});
            }
        } catch (e) {
            if (e.response?.status === 409 && e.response?.data?.erledigt) {
                aendern(fall.art, fall.id, null);
                setMeldung(`${fall.nummer} ist inzwischen erledigt.`);
                return;
            }
            aendern(fall.art, fall.id, {
                _laeuft: false,
                _fehler: e.response?.data?.meldung || "Die Antwort konnte nicht gespeichert werden. Bitte noch einmal versuchen.",
            });
        }
    };

    const ansichtWechseln = (id) => {
        router.get("/dashboard", id ? {als: id} : {}, {preserveScroll: true});
    };

    const abgleichStarten = async () => {
        setAbgleichLaeuft(true);
        try {
            await axios.post("/dashboard/vorgaenge/abgleich");
            router.reload({preserveScroll: true, onSuccess: (seite) => {
                setAuftraege(seite.props.auftraege);
                setRechnungen(seite.props.rechnungen);
            }});
            setMeldung("Abgleich mit Weclapp ist gelaufen.");
        } catch (e) {
            setMeldung("Abgleich fehlgeschlagen: " + (e.response?.data?.lauf?.meldung || e.message));
        } finally {
            setAbgleichLaeuft(false);
        }
    };

    const nichtsOffen = auftraege.length === 0 && rechnungen.length === 0;

    return (
        <div className="pv-root">
            {ansichtAls && (
                <div className="pv-demo" role="status">
                    <Icon path={mdiEyeOutline} size={0.8} className="me-2"/>
                    <strong>Demomodus: Ansicht als {ansichtAls.name}</strong>
                    <span className="ms-2">– Klicks werden nur angezeigt und nicht gespeichert, es geht keine Mail raus.</span>
                    <Tipp text="Zurück zu deiner eigenen Ansicht.">
                        <Button size="sm" variant="light" className="ms-auto" onClick={() => ansichtWechseln(null)}>Demomodus beenden</Button>
                    </Tipp>
                </div>
            )}

            <div className="pv-kopfzeile">
                <div>
                    {begruessung && <p className="pv-gruss">Hallo {begruessung}</p>}
                    <h1 className="pv-titel">
                        Meine offenen Vorgänge
                        <HelpTooltip placement="bottom">
                            Hier stehen nur deine Ausnahmen aus Weclapp: Aufträge, an denen seit einer Weile nichts passiert ist, und Rechnungen, zu denen noch kein offener Posten existiert. Ein Klick je Vorgang genügt. Geschlossen wird immer in Weclapp selbst.
                        </HelpTooltip>
                    </h1>
                    <Tipp text="Das Ordermanagement fragt Weclapp alle 15 Minuten ab, nur lesend. Fällt ein Abgleich aus, bleibt der letzte Stand stehen.">
                        <div className="pv-stand" tabIndex={0}>Stand des Abgleichs: {zeitpunkt(letzterAbgleich)}</div>
                    </Tipp>
                </div>
                {istSuperadmin && (
                    <div className="pv-werkzeuge">
                        {nurSuperadmin && (
                            <Tipp text="Sichtbarkeit steht auf „nur Superadmin“: Alle anderen sehen weiter das bisherige Dashboard, ohne Zähler am Menüpunkt.">
                                <span className="pv-chip pv-chip--info" tabIndex={0}>nur für dich sichtbar</span>
                            </Tipp>
                        )}
                        <Tipp text="Zeigt das Dashboard so, wie diese Person es sieht. Antworten werden dabei nicht gespeichert.">
                            <div className="pv-ansicht">
                                <SelectList
                                    size="sm"
                                    ariaLabel="Ansicht als"
                                    value={ansichtAls ? ansichtAls.id : ""}
                                    options={[{value: "", label: "Meine Ansicht"}, ...personen.map((p) => ({value: p.id, label: `Ansicht als ${p.name}`}))]}
                                    onChange={(v) => ansichtWechseln(v)}
                                />
                            </div>
                        </Tipp>
                        <Tipp text="Holt sofort den aktuellen Stand aus Weclapp (nur lesend), statt auf den nächsten 15-Minuten-Lauf zu warten.">
                            <Button size="sm" variant="outline-secondary" onClick={abgleichStarten} disabled={abgleichLaeuft}>
                                {abgleichLaeuft ? <Spinner animation="border" size="sm" className="me-1"/> : <Icon path={mdiRefresh} size={0.65} className="me-1"/>}
                                Abgleich jetzt
                            </Button>
                        </Tipp>
                        <Tipp text="Fristen, Ruhezeit, Zuständigkeit für Rechnungen, Wochenmail, Testempfänger und Sichtbarkeit – nur für dich.">
                            <Link href="/dashboard/vorgaenge/einstellungen" className="btn btn-sm btn-outline-secondary">
                                <Icon path={mdiCogOutline} size={0.65} className="me-1"/>Einstellungen
                            </Link>
                        </Tipp>
                    </div>
                )}
            </div>

            {meldung && (
                <div className="pv-meldung" role="status">
                    {meldung}
                    <button type="button" className="pv-meldung__zu" aria-label="Meldung schließen" onClick={() => setMeldung(null)}>×</button>
                </div>
            )}

            {nichtsOffen && (
                <p className="pv-leer">Alles erledigt – bei {ansichtAls ? ansichtAls.name : "dir"} ist gerade nichts offen.</p>
            )}

            {rechnungen.length > 0 && (
                <Card className="pv-baustein">
                    <Card.Header>
                        <h2 className="pv-baustein__titel">
                            Rechnungen ohne offenen Posten
                            <span className="pv-anzahl">{rechnungen.length}</span>
                            <HelpTooltip>
                                Rechnungen, zu denen seit mehr als einem Werktag kein offener Posten in Weclapp existiert und die nicht storniert sind. Sobald der Posten erzeugt ist, verschwindet die Rechnung beim nächsten Abgleich.
                            </HelpTooltip>
                        </h2>
                    </Card.Header>
                    <div className="pv-tabelle">
                        <Table hover className="mb-0 align-middle">
                            <thead>
                            <tr>
                                <Kopf titel="Rechnung" hilfe="Rechnungsnummer in Weclapp (bei Entwürfen die vorläufige Nummer). Ein Klick öffnet sie in Weclapp in einem neuen Tab."/>
                                <Kopf titel="Kunde" hilfe="Firma bzw. Name aus der Belegadresse der Rechnung."/>
                                <Kopf titel="Rechnungsdatum" hilfe="Datum der Rechnung in Weclapp."/>
                                <Kopf titel="Betrag" hilfe="Nettobetrag der Rechnung in Weclapp."/>
                                <Kopf titel="Status" hilfe="Status in Weclapp. Erst mit „Offener Posten erzeugt“ ist die Rechnung in der Buchhaltung angekommen."/>
                                <Kopf titel="Ohne offenen Posten" hilfe="Werktage (Montag bis Freitag) seit dem Rechnungsdatum. Feiertage zählen in dieser Stufe mit."/>
                                <Kopf titel="Bewusst noch ohne Posten?" hilfe="„Ja“ lässt die Rechnung ruhen, „Nein“ öffnet sie in Weclapp, um den Posten zu erzeugen."/>
                            </tr>
                            </thead>
                            <tbody>
                            {rechnungen.map((f) => (
                                <tr key={f.id} className={f._laeuft ? "pv-zeile--laeuft" : ""}>
                                    <td className="pv-nummer">
                                        <Tipp text="In Weclapp öffnen (neuer Tab)">
                                            <a href={weclappUrl(f)} target="_blank" rel="noopener noreferrer">{f.nummer}<Icon path={mdiOpenInNew} size={0.5} className="ms-1"/></a>
                                        </Tipp>
                                        <div className="pv-klein">{RECHNUNGSART[f.rechnungsart] || f.rechnungsart}</div>
                                    </td>
                                    <td className="pv-kunde">{f.kunde || "–"}<Zeilenvermerke fall={f}/></td>
                                    <td>{datum(f.belegdatum)}</td>
                                    <td className="text-end text-nowrap">{betrag(f.betrag, f.waehrung)}</td>
                                    <td>{RECHNUNG_STATUS[f.status] || f.status}</td>
                                    <td className="text-nowrap">{f.werktage} Werktage</td>
                                    <td><Aktionen fall={f} art="rechnung" onAntwort={onAntwort} weclappUrl={weclappUrl(f)}/></td>
                                </tr>
                            ))}
                            </tbody>
                        </Table>
                    </div>
                </Card>
            )}

            {auftraege.length > 0 && (
                <Card className="pv-baustein">
                    <Card.Header>
                        <h2 className="pv-baustein__titel">
                            Aufträge – noch berechtigt offen?
                            <span className="pv-anzahl">{auftraege.length}</span>
                            <HelpTooltip>
                                Offene Aufträge, bei denen du in Weclapp als Verantwortliche(r) eingetragen bist und an denen seit der Frist ihrer Gruppe nichts bearbeitet wurde – weder am Auftrag noch an einem seiner Lieferscheine. Gelb ab Frist, rot ab der zweiten Frist.
                            </HelpTooltip>
                        </h2>
                    </Card.Header>
                    <div className="pv-tabelle">
                        <Table hover className="mb-0 align-middle">
                            <thead>
                            <tr>
                                <Kopf titel="Auftrag" hilfe="Auftragsnummer in Weclapp, ein Klick öffnet den Auftrag in einem neuen Tab. Davor die Ampel: gelb ab der ersten Frist ohne Bearbeitung, rot ab der zweiten; die Fristen hängen von der Gruppe darunter ab."/>
                                <Kopf titel="Kunde" hilfe="Firma bzw. Name aus der Belegadresse des Auftrags."/>
                                <Kopf titel="Auftragsdatum" hilfe="Datum des Auftrags in Weclapp."/>
                                <Kopf titel="Betrag" hilfe="Nettobetrag des Auftrags in Weclapp."/>
                                <Kopf titel="Status" hilfe="Status des Auftrags in Weclapp: „In Bearbeitung“ oder „Bestätigung gedruckt“."/>
                                <Kopf titel="Versand" hilfe="Versanddienstleister des Auftrags in Weclapp. Container- und Palettenversand bekommt längere Fristen."/>
                                <Kopf titel="Zahlung" hilfe="Rechnungsstand laut Weclapp: noch nicht fakturiert (offen), fakturiert oder bezahlt."/>
                                <Kopf titel="Letzte Bearbeitung" hilfe="Jüngste Änderung am Auftrag oder an einem seiner Lieferscheine, mit Tagen seitdem."/>
                                <Kopf titel="Noch berechtigt offen?" hilfe="„Ja“ lässt den Auftrag ruhen, „Nein“ öffnet ihn in Weclapp zum Schließen."/>
                            </tr>
                            </thead>
                            <tbody>
                            {auftraege.map((f) => (
                                <tr key={f.id} className={f._laeuft ? "pv-zeile--laeuft" : ""}>
                                    <td className="pv-nummer">
                                        <Ampel fall={f}/>
                                        <Tipp text="In Weclapp öffnen (neuer Tab)">
                                            <a href={weclappUrl(f)} target="_blank" rel="noopener noreferrer">{f.nummer}<Icon path={mdiOpenInNew} size={0.5} className="ms-1"/></a>
                                        </Tipp>
                                        <div className="pv-klein">{GRUPPE[f.gruppe]}</div>
                                    </td>
                                    <td className="pv-kunde">{f.kunde || "–"}<Zeilenvermerke fall={f}/></td>
                                    <td>{datum(f.belegdatum)}</td>
                                    <td className="text-end text-nowrap">{betrag(f.betrag, f.waehrung)}</td>
                                    <td>{AUFTRAG_STATUS[f.status] || f.status}</td>
                                    <td>{f.versanddienstleister || "–"}</td>
                                    <td>
                                        <span className="text-nowrap">{f.bezahlt ? "bezahlt" : (f.fakturiert ? "fakturiert" : "offen")}</span>
                                        {f.hinweis_bezahlt && (
                                            <Tipp text="Der Auftrag ist in Weclapp fakturiert und bezahlt, steht aber noch auf offen. Er kann vermutlich geschlossen werden.">
                                                <div className="pv-chip pv-chip--info" tabIndex={0}>kann vermutlich geschlossen werden</div>
                                            </Tipp>
                                        )}
                                    </td>
                                    <td className="text-nowrap">{datum(f.letzte_bearbeitung)}<div className="pv-klein">vor {f.tage_ohne_bearbeitung} Tagen</div></td>
                                    <td><Aktionen fall={f} art="auftrag" onAntwort={onAntwort} weclappUrl={weclappUrl(f)}/></td>
                                </tr>
                            ))}
                            </tbody>
                        </Table>
                    </div>
                </Card>
            )}

            {vorfuehrung && (startAuftraege.length > 0 || startRechnungen.length > 0) && (
                <>
                    <div className="pv-beispiel">
                        <Tipp text="Zeigt die Erinnerungsmail, die die Mitarbeiter einmal pro Woche bekommen, mit den Vorgängen dieser Ansicht. Es wird nichts verschickt.">
                            <Button variant="outline-secondary" onClick={() => setMailOffen(true)}>
                                <Icon path={mdiEmailOutline} size={0.75} className="me-2"/>Beispiel der Wochenmail ansehen
                            </Button>
                        </Tipp>
                    </div>
                    <WochenmailBeispiel offen={mailOffen} onSchliessen={() => setMailOffen(false)}/>
                </>
            )}
        </div>
    );
}
